import {match, Pattern} from 'ts-pattern';

import {isNil} from 'ramda';

import type {FinancialProductUnavailableReason} from '../schemas/FinancialProductUnavailableReasonSchema';
import type {FinancialProductQuote} from '../types/FinancialProductQuote';
import type {FinancialProductQuoteCoordinates} from '../types/FinancialProductQuoteCoordinates';
import type {FinancialProductQuoteOffer} from '../types/FinancialProductQuoteOffer';

const unavailable = (reason: FinancialProductUnavailableReason): FinancialProductQuote => ({
  availability: 'UNAVAILABLE',
  unavailableReason: reason,
  calculation: null,
});

/**
 * The backend leasing quote: an exact cell lookup in its order and with its reasons, no flooring
 * and no VAT maths. COMMERCIAL customers see the published NET rate, everyone else the GROSS one;
 * the leasing down payment is the offer's own. Null when the coordinates are incomplete or
 * invalid, or the rates are withheld.
 */
export function resolveLeasingQuote(
  offer: FinancialProductQuoteOffer,
  coordinates: FinancialProductQuoteCoordinates
): FinancialProductQuote | null {
  const termMonths = coordinates.termMonths;
  const leasingTargetGroup = coordinates.leasingTargetGroup;
  const annualMileage = coordinates.annualMileage;

  if (
    isNil(termMonths) ||
    isNil(leasingTargetGroup) ||
    isNil(annualMileage) ||
    !Number.isInteger(termMonths) ||
    !Number.isInteger(annualMileage)
  ) {
    return null;
  }

  const termCells = offer.availableLeasingCells.filter((cell) => cell.termMonths === termMonths);
  const cell = termCells.find((termCell) => termCell.annualMileage === annualMileage);
  const isNet = leasingTargetGroup === 'COMMERCIAL';

  // The backend's order, the first match answers; withheld rates answer null.
  return match({
    isTargetGroupEnabled: offer.availableTargetGroups.includes(leasingTargetGroup),
    hasTermCells: termCells.length > 0,
    cell,
    monthlyPayment: isNet ? cell?.monthlyRateNet : cell?.monthlyRate,
    leasingDownPayment: offer.leasingDownPayment,
  })
    .returnType<FinancialProductQuote | null>()
    .with({isTargetGroupEnabled: false}, () => unavailable('UNSUPPORTED_TARGET_GROUP'))
    .with({hasTermCells: false}, () => unavailable('UNSUPPORTED_TERM'))
    .with({cell: Pattern.nullish}, () => unavailable('MISSING_MATRIX_CELL'))
    .with(
      {monthlyPayment: Pattern.nonNullable, leasingDownPayment: Pattern.nonNullable},
      (priced) => ({
        availability: 'AVAILABLE',
        unavailableReason: null,
        calculation: {
          termMonths,
          monthlyPayment: priced.monthlyPayment,
          downPaymentPercentage: null,
          downPaymentAmount: null,
          netCredit: null,
          grossCredit: null,
          totalInterest: null,
          totalPayable: null,
          nominalRate: null,
          apr: null,
          balloonPaymentPercentage: null,
          balloonPaymentAmount: null,
          leasingTargetGroup,
          annualMileage,
          leasingDownPayment: priced.leasingDownPayment,
          monthlyPaymentMode: isNet ? 'NET' : 'GROSS',
        },
      })
    )
    .otherwise(() => null);
}
